import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from 'lucide-react';

export function Lightbox({ images, initialIndex = 0, onClose }) {
  const [index, setIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [swipeX, setSwipeX] = useState(0);
  const touchRef = useRef({ startX: 0, startY: 0, swiping: false });

  const goPrev = useCallback(() => { setIndex(i => i > 0 ? i - 1 : images.length - 1); setZoom(1); setOffset({ x: 0, y: 0 }); setSwipeX(0); }, [images.length]);
  const goNext = useCallback(() => { setIndex(i => i < images.length - 1 ? i + 1 : 0); setZoom(1); setOffset({ x: 0, y: 0 }); setSwipeX(0); }, [images.length]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [onClose, goPrev, goNext]);

  // Touch swipe handlers
  const handleTouchStart = (e) => {
    if (zoom > 1) return;
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, swiping: false };
  };

  const handleTouchMove = (e) => {
    if (zoom > 1 || !touchRef.current.startX) return;
    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = t.clientY - touchRef.current.startY;
    // Only swipe horizontally if the gesture is more horizontal than vertical
    if (!touchRef.current.swiping && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
      touchRef.current.swiping = true;
    }
    if (touchRef.current.swiping) {
      e.preventDefault();
      setSwipeX(dx);
    }
  };

  const handleTouchEnd = () => {
    if (zoom > 1) return;
    const threshold = 60;
    if (swipeX > threshold) goPrev();
    else if (swipeX < -threshold) goNext();
    else setSwipeX(0);
    touchRef.current = { startX: 0, startY: 0, swiping: false };
  };

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.max(1, Math.min(5, z + (e.deltaY > 0 ? -0.3 : 0.3))));
  };

  const handlePointerDown = (e) => {
    if (zoom > 1) { setDragging(true); setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y }); }
  };
  const handlePointerMove = (e) => {
    if (dragging) setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handlePointerUp = () => setDragging(false);

  const toggleZoom = () => {
    if (zoom > 1) { setZoom(1); setOffset({ x: 0, y: 0 }); }
    else setZoom(2.5);
  };

  const img = images[index];
  const src = typeof img === 'string' ? img : (img?.url || img?.src);
  const alt = typeof img === 'string' ? '' : img?.alt || '';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="lightbox-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm" onClick={onClose} />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4">
        <span className="text-white/70 text-sm font-medium">{index + 1} / {images.length}</span>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom(z => Math.min(5, z + 0.5))} className="p-2 text-white/70 hover:text-white transition-colors" data-testid="lightbox-zoom-in">
            <ZoomIn size={20} />
          </button>
          <button onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); }} className="p-2 text-white/70 hover:text-white transition-colors" data-testid="lightbox-zoom-out">
            <ZoomOut size={20} />
          </button>
          <button onClick={onClose} className="p-2 text-white/70 hover:text-white transition-colors ml-2" data-testid="lightbox-close">
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button onClick={goPrev} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors" data-testid="lightbox-prev">
            <ChevronLeft size={28} />
          </button>
          <button onClick={goNext} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center transition-colors" data-testid="lightbox-next">
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="relative z-[1] w-full h-full flex items-center justify-center p-16 select-none touch-none"
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onDoubleClick={toggleZoom}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain"
          style={{
            transform: zoom > 1
              ? `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`
              : `translateX(${swipeX}px)`,
            transition: swipeX === 0 && zoom <= 1 ? 'transform 0.2s ease-out' : 'none',
          }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="absolute bottom-0 left-0 right-0 z-10 flex justify-center gap-1.5 p-4 bg-gradient-to-t from-black/60 to-transparent">
          {images.map((img, i) => {
            const thumbSrc = typeof img === 'string' ? img : (img?.url || img?.src);
            return (
              <button
                key={i}
                onClick={() => { setIndex(i); setZoom(1); setOffset({ x: 0, y: 0 }); }}
                className={`w-14 h-10 overflow-hidden flex-shrink-0 border-2 transition-all ${i === index ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
                data-testid={`lightbox-thumb-${i}`}
              >
                <img src={thumbSrc} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
